import type { Database, Json } from "@/integrations/supabase/types";

export const COACH_FIRST_TAKE_SOURCE_TYPE = "coach_first_take" as const;

export type CoachFirstTakeSourceType = typeof COACH_FIRST_TAKE_SOURCE_TYPE;
export type CoachFirstTakeStateStatus = "pending" | "saved";
export type CoachFirstTakeQueueItemTone = "critical" | "active" | "quiet";
export type CoachFirstTakeCaptureType = "text" | "voice";

export interface CoachFirstTakeQuestion {
  id: string;
  prompt: string;
}

export interface CoachFirstTakePayload {
  version: "coach_first_take_v1";
  coachCallId: string;
  promptText: string;
  captureType: CoachFirstTakeCaptureType;
  questions: CoachFirstTakeQuestion[];
  answers: Record<string, string>;
}

export type CoachFirstTakeRecordRow =
  Database["public"]["Tables"]["coach_leader_session_records"]["Row"];

export interface CoachFirstTakeRecord extends CoachFirstTakeRecordRow {
  coachCallId: string;
  promptText: string;
  captureType: CoachFirstTakeCaptureType;
  questions: CoachFirstTakeQuestion[];
  answers: Record<string, string>;
}

export interface CoachFirstTakeContinuity {
  recordId: string;
  coachCallId: string;
  meetingId: string | null;
  leaderId: string;
  createdAt: string;
  sourceType: CoachFirstTakeSourceType;
  status: CoachFirstTakeStateStatus;
  summary: string;
  promptText: string;
  coachNote: string | null;
  captureType: CoachFirstTakeCaptureType;
  publishedToLeader: boolean;
}

export interface CoachFirstTakeQueueItem {
  label: string;
  detail: string;
  tone: CoachFirstTakeQueueItemTone;
}

export interface CoachFirstTakeState {
  status: CoachFirstTakeStateStatus;
  title: string;
  detail: string;
  continuity: CoachFirstTakeContinuity | null;
}

function normalizeLine(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function hasSubstantiveContent(value: string): boolean {
  return normalizeLine(
    value.replace(/\p{Default_Ignorable_Code_Point}/gu, ""),
  ).length > 0;
}

export function hasCoachFirstTakeResponse(input: {
  answers: Readonly<Record<string, string>>;
  coachNote: string | null | undefined;
}): boolean {
  return Object.values(input.answers).some(hasSubstantiveContent) ||
    hasSubstantiveContent(input.coachNote ?? "");
}

function isQuestionArray(value: unknown): value is CoachFirstTakeQuestion[] {
  return Array.isArray(value) && value.every((item) => {
    return (
      typeof item === "object" &&
      item !== null &&
      typeof (item as { id?: unknown }).id === "string" &&
      typeof (item as { prompt?: unknown }).prompt === "string"
    );
  });
}

function isAnswerMap(value: unknown): value is Record<string, string> {
  return typeof value === "object" && value !== null && !Array.isArray(value) && Object.values(value).every((entry) => typeof entry === "string");
}

function parsePayload(value: Json | null): CoachFirstTakePayload | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const payload = value as Record<string, unknown>;
  if (payload.version !== "coach_first_take_v1") return null;
  if (typeof payload.coachCallId !== "string") return null;
  if (typeof payload.promptText !== "string") return null;
  if (payload.captureType !== "text" && payload.captureType !== "voice") return null;
  if (!isQuestionArray(payload.questions)) return null;
  if (!isAnswerMap(payload.answers)) return null;

  return {
    version: "coach_first_take_v1",
    coachCallId: payload.coachCallId,
    promptText: payload.promptText,
    captureType: payload.captureType,
    questions: payload.questions,
    answers: payload.answers,
  };
}

function buildSummary(record: CoachFirstTakeRecord): string {
  const coachNote = record.coach_note ? normalizeLine(record.coach_note) : "";
  return coachNote || "Saved first-take capture ready for deeper review.";
}

export function buildCoachFirstTakePayload(input: {
  coachCallId: string;
  promptText: string;
  captureType: CoachFirstTakeCaptureType;
  questions: CoachFirstTakeQuestion[];
  answers: Record<string, string>;
}): CoachFirstTakePayload {
  return {
    version: "coach_first_take_v1",
    coachCallId: input.coachCallId,
    promptText: input.promptText,
    captureType: input.captureType,
    questions: input.questions,
    answers: input.answers,
  };
}

export function normalizeCoachFirstTakeRecord(
  record: CoachFirstTakeRecordRow | null | undefined,
): CoachFirstTakeRecord | null {
  if (!record || record.source_type !== COACH_FIRST_TAKE_SOURCE_TYPE) return null;
  const payload = parsePayload(record.coach_review_v1);
  if (!payload) return null;

  return {
    ...record,
    coachCallId: payload.coachCallId,
    promptText: payload.promptText,
    captureType: payload.captureType,
    questions: payload.questions,
    answers: payload.answers,
  };
}

export function buildCoachFirstTakeContinuity(
  record: CoachFirstTakeRecord,
): CoachFirstTakeContinuity {
  return {
    recordId: record.id,
    coachCallId: record.coachCallId,
    meetingId: record.meeting_id,
    leaderId: record.leader_id,
    createdAt: record.created_at,
    sourceType: COACH_FIRST_TAKE_SOURCE_TYPE,
    status: "saved",
    summary: buildSummary(record),
    promptText: record.promptText,
    coachNote: record.coach_note ? normalizeLine(record.coach_note) : null,
    captureType: record.captureType,
    publishedToLeader: record.published_to_leader,
  };
}

export function buildCoachFirstTakeQueueItem(
  record: CoachFirstTakeRecord,
): CoachFirstTakeQueueItem {
  const continuity = buildCoachFirstTakeContinuity(record);

  return {
    label: "Your first take",
    detail: continuity.summary,
    tone: continuity.publishedToLeader ? "quiet" : "active",
  };
}

export function buildCoachFirstTakeState(input: {
  savedRecord?: CoachFirstTakeRecord | null;
  isDueNow?: boolean;
}): CoachFirstTakeState | null {
  if (input.savedRecord) {
    const continuity = buildCoachFirstTakeContinuity(input.savedRecord);
    return {
      status: "saved",
      title: "Your first take",
      detail: continuity.summary,
      continuity,
    };
  }

  if (input.isDueNow) {
    return {
      status: "pending",
      title: "Your first take",
      detail: "The first-take capture is due now.",
      continuity: null,
    };
  }

  return null;
}
